<template>
    <div id="print-queue-container">
        <DispenseStatusComponent />
        <card :title="pageTitle">
            <b-row>
                <b-col>
                    <b-badge v-if="!storeFilter?.id" variant="warning" class="pull-right throb-badge"
                        style="margin-top:-2px;">
                        <i class="fas fa-exclamation-triangle" />
                        Please Select a Store
                    </b-badge>
                    <SearchComponent :id="storeFilter?.id" v-model="storeFilter" label="Store" search-u-r-l="/Store"
                        :params="{active: true}" prepopulate />
                </b-col>
                <b-col>
                    <SearchComponent :id="programFilter?.id" v-model="programFilter" label="Program"
                        search-u-r-l="/Program" :params="{ active: true }" clearable prepopulate />
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="6">
                    <b-row align-h="between">
                        <b-col cols="4">
                            <checkbox v-model="showOutOfStocks">Show Out Of Stocks</checkbox>
                            <checkbox v-model="showConsignment">Show Consignment</checkbox>
                            <checkbox v-model="showPrinted">Show Recently Printed</checkbox>
                        </b-col>
                        <b-col cols="6">
                            <b-form-group id="out-of-stock-group" label-cols-sm="1" content-cols-sm="11"
                                label-cols-md="1" content-cols-md="6">
                                <b-form-input v-model="outOfStockValue" type="text" placeholder="Mark Out Of Stock..."
                                    @keydown.enter.native="sendOutOfStock" />
                            </b-form-group>
                            <b-form-group v-if="isStalePrintQueue" id="days-old-group" class="mt-2" label-cols-sm="6"
                                content-cols-sm="6" label-cols-md="3" content-cols-md="4" label-align="right"
                                label="Days Old" label-for="days-old-input">
                                <b-form-input id="days-old-input" class="m-0" v-model="oldQueueDays" type="number"
                                    min="0" v-b-tooltip.hover
                                    title="The number of days since the item has been printed." />
                            </b-form-group>
                        </b-col>
                        <!--                        <b-col cols="2" v-if="needsOverride">-->
                        <!--                            <checkbox v-model="shouldOverride">Override Out Of Stock</checkbox>-->
                        <!--                        </b-col>-->
                    </b-row>
                    <b-button v-b-modal.print-queue-modal variant="info">Stats</b-button>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-button variant="success" :disabled="!(storeFilter && storeFilter.id)"
                        @click="$bvModal.show('print-rx-rf-modal')">
                        Print by Rx/Rf
                    </b-button>
                    <b-button variant="primary" @click="$bvModal.show('print-badge')">
                        Reprint Badge
                    </b-button>
                    <b-button variant="dark" @click="clearFilters">
                        Clear Filters
                    </b-button>
                    <b-button variant="info" @click="$bvModal.show('column-customize-modal')">
                        <i class="fa fa-cog"></i> Customize Columns
                    </b-button>
                </b-col>
            </b-row>
            <hr>
            <div v-if="filteredPrescriptions && filteredPrescriptions.length" class="mb-2 print-actions glass-pane">
                <div class="d-flex align-items-center">
                    <div class="d-flex flex-fill align-items-center">
                        <div class="mr-2">
                            <b-button variant="info" @click="selectOrders">Select {{ N_ORDERS_SELECTION }}</b-button>
                        </div>
                        <div class="mr-2 d-flex" v-if="!isLoadingPrinters">
                            <b-dropdown :text="prettyPrinterName(activeLocalPrinter)" variant="outline-primary">
                                <b-dropdown-item-button :active="!activeLocalPrinter"
                                    @click="activeLocalPrinter = null">
                                    <i class="fa fa-building"></i>
                                    Store Default Printer
                                </b-dropdown-item-button>
                                <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                                    :active="p.name === activeLocalPrinter?.name" @click="activeLocalPrinter = p">
                                    <i class="fa fa-print"></i>
                                    {{ prettyPrinterName(p) }}
                                    <div class="d-flex" style="gap: 6px;">
                                        <small v-if="p.default">Local Default</small>
                                        <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                                    </div>
                                </b-dropdown-item-button>
                                <b-dropdown-item disabled v-if="!hasLocalPrinters">
                                    <i class="fa fa-info-circle" />
                                    To print to local printers, install<br>the Scriptly Client Service
                                </b-dropdown-item>
                            </b-dropdown>

                            <b-button variant="success" @click="print(false)" :disabled="!allowPrinting">
                                Print
                            </b-button>
                            <b-button v-if="allowCentralFill" variant="success" @click="print(true)"
                                :disabled="!allowPrinting">
                                Send to Central Fill
                            </b-button>
                        </div>
                    </div>
                    <div class="d-flex align-items-center">
                        <b-overlay :show="isMarkingInStock" class="d-inline-block" spinner-small>
                            <b-button v-if="showOutOfStocks" size="sm" variant="primary" :disabled="!selectedCount"
                                @click="markInStock" class="m-0">
                                Mark as In Stock
                                <template v-if="selectedCount">({{ selectedCount }})</template>
                            </b-button>
                        </b-overlay>
                        <b-button variant="success" size="sm" :disabled="!allowPrinting"
                            @click="$bvModal.show('label-reprint-modal')">
                            Print Label w/o Patient Ed
                        </b-button>
                        <b-button variant="danger" size="sm" :disabled="!allowPrinting" @click="removeWithoutPrinting">
                            Remove Selected w/o Printing
                        </b-button>
                        <b-button v-b-modal="'external-transfer-modal'" size="sm" variant="info" :disabled="!allowPrinting">
                            Transfer Out...
                        </b-button>
                    </div>
                </div>
            </div>

            <PrescriptionsListComponent v-if="storeFilter?.id" :prescriptions="filteredPrescriptions"
                :selected="selectedRxsIn" mode="print" showPatientName showAddOns :showVoidBox="false"
                :additionalFields="additionalFields" :fieldsKeysToRemove="fieldsToRemove" defaultSort="deliveryCode"
                :current-sort.sync="currentSort" :current-desc.sync="currentDesc" :color-codes="colorCodeTable"
                :custom-sort="customSort" :is-loading="isLoading" @selection-changed="rxSelectionChanged"
                :rx-id-link="true" />
        </card>

        <b-modal id="print-queue-modal" size="xl" cancel-title="Close" title="Print Stats" @shown="printQueueModalOpen = true" @hidden="printQueueModalOpen = false">
            <template #modal-footer="modal">
                <b-row>
                    <b-col>
                        <b-button @click="modal.close()">Close</b-button>
                    </b-col>
                </b-row>
            </template>
            <PrintQueueTable v-if="printQueueModalOpen" :status="status.printQueueStatus" :s-list="status.storeList"
                @link-clicked="$bvModal.hide('print-queue-modal')" />
        </b-modal>
        <b-modal id="label-reprint-modal" title="Reprint Label">
            <b-row>
                <b-col>
                    <fg-input type="text" placeholder="Enter IP Address Here" v-model="printerAddress" />
                </b-col>
            </b-row>
            <template #modal-footer>
                <b-button @click="printWithoutPatEd" variant="success">Print</b-button>
            </template>
        </b-modal>
        <b-modal id="print-rx-rf-modal" title="Print by Rx/Rf" no-fade centered>
            <b-row class="mb-2">
                <b-col>
                    <fg-input label="Store" disabled :value="storeFilter" />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <fg-input label="RxNumber" type="number" v-model.number="rxToPrint.rxNumber" />
                </b-col>
                <b-col>
                    <fg-input label="RfNumber" type="number" v-model.number="rxToPrint.rfNumber" />
                </b-col>
            </b-row>
            <template #modal-footer>
                <div class="flex-fill">
                    <b-button variant="success" @click="printByRxRf"
                        :disabled="!(rxToPrint.rxNumber) || isPrintingRxRf">
                        Print
                    </b-button>
                </div>
            </template>
        </b-modal>
        <b-modal id="print-badge" title="Print Badge" no-fade>
            <b-row>
                <b-col>
                    <SearchComponent v-model="storeFilter" label="Store" search-u-r-l="/Store" :params="{active: true}" prepopulate />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-dropdown :text="activeBadgePrinter ? prettyPrinterName(activeBadgePrinter) : 'IP Address'"
                        variant="outline-primary">
                        <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                            :active="p.name === activeBadgePrinter?.name" @click="activeBadgePrinter = p">
                            <i class="fa fa-print"></i>
                            {{ prettyPrinterName(p) }}
                            <div class="d-flex" style="gap: 6px;">
                                <small v-if="p.default">Local Default</small>
                                <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                            </div>
                        </b-dropdown-item-button>
                        <b-dropdown-item disabled v-if="!hasLocalPrinters">
                            <i class="fa fa-info-circle" />
                            To print to local printers, install<br>the Scriptly Client Service
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item-button :active="!activeBadgePrinter" @click="activeBadgePrinter = null">
                            <i class="fa fa-globe"></i>
                            IP Address...
                        </b-dropdown-item-button>
                    </b-dropdown>
                    <fg-input v-if="!activeBadgePrinter" label="IP Address:" type="text" v-model="ipAddress" />
                </b-col>
            </b-row>
            <template #modal-footer>
                <b-button variant="success" @click="printBadge"
                    :disabled="!(storeFilter && storeFilter.id) || !(ipAddress || activeBadgePrinter) || isPrintingBadge">
                    Print
                </b-button>
            </template>
        </b-modal>
        <b-modal v-model="showPrintingModal" size="sm" no-fade centered body-class="text-center" hide-footer hide-header
            no-close-on-backdrop no-close-on-esc>
            <div class="printers-loading mb-2 d-flex justify-content-center align-items-center">
                <i class="far fa-file-alt" />
                <i class="far fa-file-alt" />
                <i class="far fa-file-alt" />
                <i class="fa fa-print" />
            </div>
            <strong v-if="!printProgress">Printing...</strong>
            <div v-else>
                <strong>Printing...</strong>
                <div class="mt-2">
                    <b-progress :value="printProgress.successCount + printProgress.failureCount"
                                :max="printProgress.totalCount"
                                :variant="printProgress.failureCount > 0 ? 'warning' : 'success'"
                                height="20px" />
                </div>
                <div class="mt-2">
                    <small>
                        <span class="text-success">{{ printProgress.successCount }} completed</span>
                        <span v-if="printProgress.failureCount > 0" class="text-danger ml-2">{{ printProgress.failureCount }} failed</span>
                        <span class="text-muted ml-2">{{ printProgress.remainingCount }} remaining</span>
                    </small>
                </div>
            </div>
        </b-modal>
        <!-- External Transfer Modal -->
        <ExternalTransferModal
            :prescriptions="selectedRxsOut"
            :store="storeFilter"
            @transferred="onTransferCompleted" />

        <!-- Column Customize Modal -->
        <b-modal id="column-customize-modal" title="Customize Table Columns" size="lg" @ok="saveColumnPreferences">
            <div class="mb-3">
                <h6>Select which columns to display in the print queue table:</h6>
            </div>

            <div class="grid">
                <!-- Core table columns -->
                <h6 style="grid-column: 1 / -1;" class="mt-3 mb-2">Core Columns</h6>
                <b-form-checkbox v-model="columnVisibility.hasAddons">Add-ons</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.productNameShort">Drug</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.patientName">Patient</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.storeName">Store</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.rxId">Rx Id</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.daySupply">Day Supply</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.dispensed">Dispensed</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.qtyLeft">Quantity Left</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.fillDate">Fill Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.patientPay">Patient Pay</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.programName">Program</b-form-checkbox>
                
                <!-- Additional columns -->
                <h6 style="grid-column: 1 / -1;" class="mt-3 mb-2">Additional Columns</h6>
                <b-form-checkbox v-model="columnVisibility.promisedDate">Promised Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.ndc">NDC</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.rxIdWithExpirationWarning">Rx Id (with expiration)</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.transferToPharmacy">Transfer To Pharmacy</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.orderDate">Order Date</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.addressState">State</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.addressZip">Zip Code</b-form-checkbox>
                <b-form-checkbox v-model="columnVisibility.drugsFilledDate">Filled Date</b-form-checkbox>
            </div>

            <template #modal-footer="{ ok, cancel }">
                <b-button variant="secondary" @click="cancel()">Cancel</b-button>
                <b-button variant="primary" @click="ok()">Save Preferences</b-button>
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { DatePicker } from "element-ui";
import SearchComponent from '@/components/SearchComponent.vue';
import PrescriptionsListComponent from '@/components/Prescription/PrescriptionsListComponent.vue';
import { Prescription } from '@/models/Prescription';
import { Program } from '@/models/Program';
import { Store } from '@/models/Store';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import moment from "moment";
import { DeliveryCode } from "@/models/DeliveryCode";
import { OrderLabelData } from "@/models/OrderLabelData";
import PrintQueueTable from "@/components/Dashboard/PrintQueueTable.vue";
import { DashboardStatus } from "@/models/DashboardStatus";
import DispenseStatusComponent from "@/pages/Dashboard/Dashboard/DispenseStatusComponent.vue";
import ExternalTransferModal from "@/components/Transfer/ExternalTransferModal.vue";

const OldQueueDaysKey = 'stale_print_queue_old_queue_days';

@Component({
    name: "PrintQueuePage",
    components: {
        DispenseStatusComponent,
        PrintQueueTable,
        SearchComponent,
        [DatePicker.name]: DatePicker,
        PrescriptionsListComponent,
        Checkbox,
        ExternalTransferModal,
    },
})
export default class PrintQueuePage extends Vue {
    private prescriptions: Array<OrderLabelData> = [];
    private selectedRxsIn: Array<Prescription> = [];
    private selectedRxsOut: Array<Prescription> = [];
    private printerAddress: string = "";
    private rxToPrint: Prescription = new Prescription();
    private N_ORDERS_SELECTION = 10;
    private showOutOfStocks = false;
    private showConsignment = false;
    private showPrinted = false;
    private oldQueueDays: number = 0;
    private outOfStockValue = '';
    private programFilter: Program = new Program();
    private storeFilter: Store = new Store();
    private shouldOverride: boolean = false;
    protected needsOverride: boolean = false;
    private ipAddress: string = "";

    protected isLoading: boolean = false;
    protected isPrinting: boolean = false;
    protected isPrintingRxRf: boolean = false;
    protected isPrintingBadge: boolean = false;

    private localPrinters: any[] = [];
    private activeLocalPrinter: any | null = null;
    private activeBadgePrinter: any | null = null;
    private isLoadingPrinters: boolean = false;
    private isMarkingInStock: boolean = false;
    private printQueueModalOpen: boolean = false;
    private printProgress: { successCount: number; failureCount: number; remainingCount: number; totalCount: number } | null = null;

    get fieldsToRemove(): Array<string> {
        const fieldsToHide: Array<string> = [];
        
        // Always remove these by default (as they were in the original code)
        fieldsToHide.push('qtyLeft', 'rxId');
        
        // Remove core fields based on visibility settings
        if (!this.columnVisibility.hasAddons) fieldsToHide.push('hasAddons');
        if (!this.columnVisibility.productNameShort) fieldsToHide.push('productNameShort');
        if (!this.columnVisibility.patientName) fieldsToHide.push('patientName');
        if (!this.columnVisibility.storeName) fieldsToHide.push('storeName');
        if (!this.columnVisibility.daySupply) fieldsToHide.push('daySupply');
        if (!this.columnVisibility.dispensed) fieldsToHide.push('dispensed');
        if (!this.columnVisibility.fillDate) fieldsToHide.push('fillDate');
        if (!this.columnVisibility.patientPay) fieldsToHide.push('patientPay');
        if (!this.columnVisibility.programName) fieldsToHide.push('programName');
        if (!this.columnVisibility.edit) fieldsToHide.push('edit');
        if (!this.columnVisibility.select) fieldsToHide.push('select');
        
        return fieldsToHide;
    }
    private priorityCodes = [DeliveryCode.Overnight, DeliveryCode.StandardOvernight];
    private currentSort: string = "";
    private currentDesc: boolean = true;

    // Column visibility preferences
    private columnVisibility: any = {
        // Core table fields from rxFields
        select: true,
        hasAddons: true,
        productNameShort: true,
        patientName: true,
        storeName: true,
        rxId: true,
        daySupply: true,
        dispensed: true,
        qtyLeft: true,
        fillDate: true,
        patientPay: true,
        programName: true,
        edit: true,
        // Additional fields
        transferToPharmacy: true,
        orderDate: true,
        addressState: true,
        addressZip: true,
        drugsFilledDate: true,
        ndc: true,
        rxIdWithExpirationWarning: true,
        promisedDate: true,
    };

    get pageMode() {
        return this.$route.name;
    }

    get pageTitle(): string {
        if (this.isStalePrintQueue) {
            return this.$pageNames.stalePrintQueue ?? "Stale Print Queue";
        }
        return this.$pageNames.printQueue ?? "Print Queue";
    }
    get isStalePrintQueue(): boolean {
        return this.pageMode == "StalePrintQueue";
    }

    get hasPromised(): Boolean {
        return !!this.prescriptions?.find(i => i.promisedDate);
    }

    get showTransferToPharmacy(): boolean {
        return this.filteredPrescriptions.some(p => p.transferToPharmacy?.length ?? 0 > 0);
    }

    get additionalFields() {
        const fields = [];

        if (this.columnVisibility.transferToPharmacy) {
            fields.push({
                index: null,
                field: { key: "transferToPharmacy", label: "Transfer To Pharmacy", sortable: true, class: this.showTransferToPharmacy ? "" : "d-none", },
            });
        }

        if (this.columnVisibility.orderDate) {
            fields.push({
                index: null,
                field: { key: "orderDate", label: "Order Date", sortable: true, formatter: this.dateFormatter },
            });
        }

        if (this.columnVisibility.addressState) {
            fields.push({
                index: null,
                field: { key: "addressState", label: "State", sortable: true },
            });
        }

        if (this.columnVisibility.addressZip) {
            fields.push({
                index: null,
                field: { key: "addressZip", label: "Zip Code", sortable: true },
            });
        }

        if (this.columnVisibility.drugsFilledDate) {
            fields.push({
                index: null,
                field: { key: "drugsFilledDate", label: "Filled", sortable: true, formatter: this.filledDateFormatter },
            });
        }

        if (this.columnVisibility.ndc) {
            fields.push({
                index: 1,
                field: { key: "ndc", label: "NDC", sortable: true, formatter: this.ndcFormatter },
            });
        }

        if (this.columnVisibility.rxIdWithExpirationWarning) {
            fields.push({
                index: 5,
                field: { key: "rxIdWithExpirationWarning", label: "Rx Id" },
            });
        }

        if (this.hasPromised && this.columnVisibility.promisedDate) {
            fields.push({
                index: 0,
                field: {
                    key: "promisedDate",
                    label: "Promised",
                    sortable: true,
                    formatter: (value: any) => !value ? "" : value.toString(),
                },
            });
        }

        return fields;
    }

    get persistentOldQueueDays(): number {
        const objStr = localStorage.getItem(OldQueueDaysKey);
        if (objStr) return JSON.parse(objStr);
        return 0;
    }

    beforeMount() {
        console.log(`%cBefore Mounting ${this.$options.name}`, 'color: greenyellow');
        this.oldQueueDays = this.persistentOldQueueDays;
        this.loadColumnPreferences();
    }

    @Watch("oldQueueDays")
    changedOldQueueDays(val: number, _oldVal: any) {
        localStorage.setItem(OldQueueDaysKey, JSON.stringify(val));
    }

    @Watch("currentSort")
    @Watch("currentDesc")
    currentSortUpdated(_newValue: string, _oldValue: string) {
        let scripts = this.prescriptions.sort((a, b) => this.customSort(a, b, this.currentSort, this.currentDesc));
        if (this.currentDesc) scripts = scripts.reverse();
        this.prescriptions = scripts;
    }

    dateFormatter(value: Date, _key: string, _item: any) {
        if (!value) {
            return "";
        }
        return moment.utc(value).format("L");
    }

    filledDateFormatter(value: Date, _key: string, item: any) {
        if (!value || item.fillDate > item.drugsFilledDate) {
            return "";
        }
        return moment.utc(value).format("L");
    }

    ndcFormatter(value: string) {
        if (value == null) {
            return value;
        }
        return `${value.substr(0, 5)}-${value.substr(5, 4)}-${value.substr(9)}`;
    };

    get filteredPrescriptions() {
        // Only apply the Days Old filter on the Stale Print Queue page
        if (this.isStalePrintQueue && this.oldQueueDays > 0) {
            return this.prescriptions.filter(s => moment(s.printed) < moment().add(-1 * this.oldQueueDays, "days"));
        }

        return this.prescriptions;
    }

    async loadOrders() {
        if (!this.storeFilter.id) return;
        try {
            this.isLoading = true;
            let url = `/Print/prescriptions-pending/${this.storeFilter.id}/${(this.programFilter?.id || '')}`;
            if (this.pageMode == "StalePrintQueue") {
                url = `/Print/GetStalePrintQueue/${this.storeFilter.id}/${(this.programFilter?.id || '')}`;
            }
            if (this.showPrinted) {
                url = `/Print/recently-printed/${this.storeFilter.id}/${(this.programFilter?.id || '')}`;
            }

            //Get patient's prescriptions and format the data to be shown in the grid.
            const response = await Axios.get<OrderLabelData[]>(url, {
                params: {
                    outOfStock: this.showOutOfStocks,
                    isConsignment: this.showConsignment,
                },
            });
            let scripts: OrderLabelData[] = response.data.map((rx: any) => new OrderLabelData(rx));
            scripts = scripts.sort(this.sortDeliveryCode);
            this.prescriptions = scripts;
        } catch (err) {
            const error = err as AxiosError;
            console.warn("Error while loading queue.", { error, response: error?.response });
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isLoading = false;
        }
    }

    private toString(value: any): string {
        if (value === null || typeof value === "undefined") {
            return "";
        }
        if (value instanceof Object) {
            return Object.keys(value)
                .sort()
                .map(key => this.toString(value[key]))
                .join(" ");
        }
        return String(value);
    }

    get customSort(): (a: Prescription, b: Prescription, sortBy?: string, sortDesc?: boolean, formatter?: Function, compareOptions?: any, compareLocale?: any) => number {
        return (a: Prescription, b: Prescription, sortBy?: string, _sortDesc?: boolean, _formatter?: Function, compareOptions?: any, compareLocale?: any) => {
            if (sortBy == undefined || sortBy === "deliveryCode" || sortBy === "") {
                return this.sortDeliveryCode(b, a);
            }
            const aSort = (a as any)[sortBy];
            const bSort = (b as any)[sortBy];
            if ((typeof aSort === "number" && typeof bSort === "number") ||
                (aSort instanceof Date && bSort instanceof Date)) {
                return a < b ? -1 : a > b ? 1 : 0;
            } else {
                return this.toString(aSort).localeCompare(this.toString(bSort), compareLocale, compareOptions);
            }
        };
    }

    get sortDeliveryCode(): (a: Prescription, b: Prescription) => number {
        return (a: Prescription, b: Prescription) => {
            if (this.priorityCodes.includes(b.deliveryCode)) return 1;
            if (this.priorityCodes.includes(a.deliveryCode)) return -1;
            if (b.deliveryCode == DeliveryCode.WillCall) return 1;
            if (a.deliveryCode == DeliveryCode.WillCall) return -1;
            return 0;
        };
    }

    get lastStore(): any {
        const objStr = localStorage.getItem('printQueue_lastStore');
        if (objStr)
            return JSON.parse(objStr);
        else return null;
    }

    get status(): DashboardStatus {
        if (this.$root.$data.dashboardData) {
            return this.$root.$data.dashboardData as DashboardStatus;
        }
        return new DashboardStatus();
    }

    @Watch('storeFilter')
    lastStoreChanged(val1: any, _val2: any) {
        if (val1 == new Store()) return;
        localStorage.setItem('printQueue_lastStore', JSON.stringify(val1));
    }

    mounted() {
        this.storeFilter = new Store(this.lastStore) ?? new Store();
        this.setupFromQuery();
        this.getLocalPrinters();
    }

    setupFromQuery() {
        const query = this.$route.query;
        if (Object.keys(query).findIndex(v => v == "storeId") != -1 && this.storeFilter?.id != Number(query.storeId)) {
            if (query.storeId) {
                this.storeFilter = new Store({ id: Number(query.storeId) } as Store);
            }
        }

        if (Object.keys(query).findIndex(v => v == "programId") != -1 && this.programFilter?.id != Number(query.programId)) {
            if (query.programId) {
                this.programFilter = new Program({ id: Number(query.programId) } as Program);
            } else {
                this.programFilter = new Program();
            }
        }

        if (Object.keys(query).findIndex(v => v == "oos") != -1 && this.showOutOfStocks != (query.oos.toString() == "true")) {
            this.showOutOfStocks = query.oos.toString() == "true";
        }

        if (Object.keys(query).findIndex(v => v == "recent") != -1 && this.showPrinted != (query.recent.toString() == "true")) {
            this.showPrinted = query.recent.toString() == "true";
        }

        if (Object.keys(query).findIndex(v => v == "consignment") != -1 && this.showConsignment != (query.consignment.toString() == "true")) {
            this.showConsignment = query.consignment.toString() == "true";
        }
    }

    @Watch('$route.query')
    queryChanged(_newValue: any, _oldValue: any) {
        this.setupFromQuery();
    }

    @Watch('showOutOfStocks')
    oosChanged(newValue: boolean, oldValue: boolean) {
        const oos = this.$route.query.oos == "true";
        if (newValue != oldValue && newValue != oos) {
            this.$router.replace({
                name: this.$route.name ?? "",
                query: { ...this.$route.query, oos: newValue.toString(), preserveScroll: "1" }
            }).catch(() => {
                // Ignore navigation duplicated errors
            });
        }
    }

    @Watch('showPrinted')
    printedChanged(newValue: boolean, oldValue: boolean) {
        const recent = this.$route.query.recent == "true";
        if (newValue != oldValue && newValue != recent) {
            this.$router.replace({
                name: this.$route.name ?? "",
                query: { ...this.$route.query, recent: newValue.toString(), preserveScroll: "1" }
            });
        }
    }

    @Watch('storeFilter')
    storeChange(newValue: Store, oldValue: Store) {
        if (newValue.id != oldValue.id && newValue.id != Number(this.$route.query.storeId)) {
            this.$router.replace({
                name: this.$route.name ?? "",
                query: { ...this.$route.query, storeId: this.storeFilter.id.toString(), preserveScroll: "1" }
            });
        }
    }

    @Watch('programFilter')
    programChange(newValue: Program, oldValue: Program) {
        if (newValue?.id != oldValue?.id && newValue?.id != Number(this.$route.query?.programId)) {
            this.$router.replace({
                name: this.$route.name ?? "",
                query: { ...this.$route.query, programId: newValue?.id?.toString(), preserveScroll: "1" }
            });
        }
    }

    @Watch('showConsignment')
    @Watch('showOutOfStocks')
    @Watch('storeFilter')
    @Watch('programFilter')
    @Watch('showPrinted')
    reloadOrders(_newValue: any, _oldValue: any) {
        this.loadOrders();
    }

    @Watch('pageMode') pageModeChanged() {
        this.clearFilters();
        this.prescriptions = [];
        this.selectedRxsIn = [];
        this.selectedRxsOut = [];
        this.outOfStockValue = '';
        this.rxToPrint = new Prescription();
    }

    @Watch('activeLocalPrinter')
    activeLocalPrinterChanged() {
        localStorage.setItem("active-printer", this.activeLocalPrinter?.name);
    }

    @Watch('activeBadgePrinter')
    activeBadgePrinterChanged() {
        localStorage.setItem("active-badge-printer", this.activeBadgePrinter?.name);
    }

    get allowPrinting() {
        return this.selectedRxsOut?.length;
    }

    get allowCentralFill() {
        const centralFill = this?.$config?.VITE_APP_CENTRAL_FILL_ENABLED == "true";
        return centralFill;
    }

    selectOrders() {
        this.selectedRxsIn = this.prescriptions.slice(0, this.N_ORDERS_SELECTION);
        this.selectedRxsOut = this.selectedRxsIn;
    }

    rxSelectionChanged(rows: Array<Prescription>) {
        this.selectedRxsOut = rows;
    }

    async printWithoutPatEd() {
        try {
            this.isPrinting = true;
            for await (const script of this.selectedRxsOut) {
                await Axios.post(`/Print/SkipPatEd/${script.storeID}/${script.rxNumber}/${script.rfNumber}/${script.orderId}?ipAddress=${this.printerAddress}`);
            }

            await this.loadOrders();
            this.isPrinting = false;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async print(centralFill: boolean = false) {
        this.isPrinting = true;
        //Get patient's prescriptions and format the data to be shown in the grid.
        const ids = {
            PrintMode: this.showPrinted ? "RecentlyPrinted" : this.pageMode,
            Labels: this.selectedRxsOut.map(rx => {
                return {
                    OrderId: rx.orderId,
                    StoreId: rx.storeID,
                    RxNumber: rx.rxNumber,
                    RfNumber: rx.rfNumber,
                    ProgramId: rx.programID,
                };
            }),
        };

        const localPrint = !!this.activeLocalPrinter;

        //Get patient's prescriptions and format the data to be shown in the grid.
        try {
            const response = await Axios.post(`/api/Print/prescriptions-pending`, ids, { params: { localPrint, centralFill } });

            if (localPrint) {
                await this.sendToLocalPrint(response.data.toPrint, true);
                return;
            }

            const printedLabels = response.data.printedLabels || 0;
            const sentLabels = ids.Labels.length || 0;
            // Notify the number of labels printed
            if (printedLabels) {
                this.$notification(NotificationOptions.notificationOptionsPreset(`Printed ${printedLabels} labels.`, NotificationOptions.NotificationTypes.success));
            } else if (sentLabels > printedLabels) {
                this.$notification(NotificationOptions.notificationOptionsPreset(`${sentLabels - printedLabels} labels were not printed.`, NotificationOptions.NotificationTypes.danger));
                console.error("Errors while printing", response.data);
            }
            // Reload Print Queue.
            await this.loadOrders();
        } catch (error) {
            console.warn(error);
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.isPrinting = false;
        }
    }

    async sendToLocalPrint(jobsToPrint: any, markAsPrinted: boolean) {
        try {
            // Reset progress at the start
            this.printProgress = null;

            const printerName = this.activeLocalPrinter.name;
            const apiBaseUrl = this.$http.defaults.baseURL?.replace("/api", "");

            // Get the current auth token to pass to the client service
            const authToken = await this.getAuthToken();

            // Generate unique job ID for this print request
            const jobId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            const localRequest = {
                printerName,
                jobs: jobsToPrint,
                apiBaseUrl,
                markAsPrinted,
                authToken,
                jobId,
            };

            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Use WebSocket connection through shared worker
                const result = await new Promise<{ success: boolean; data?: any }>((resolve) => {
                    const handleMessage = async (e: MessageEvent) => {
                        if (e.data[0] === "clientServicePrintProgress") {
                            const progress = e.data[1];
                            // Only handle progress for our job
                            if (progress.jobId === jobId) {
                                console.log(`Print progress: ${progress.successCount}/${progress.totalCount} completed, ${progress.failureCount} failed, ${progress.remainingCount} remaining`);
                                // Update the progress display
                                this.printProgress = {
                                    successCount: progress.successCount,
                                    failureCount: progress.failureCount,
                                    remainingCount: progress.remainingCount,
                                    totalCount: progress.totalCount
                                };

                                // Show notification immediately if a job failed to print
                                if (progress.failedJob && progress.failureMessage) {
                                    const jobId = `${progress.failedJob.storeId}-${progress.failedJob.rxNumber}-${progress.failedJob.rfNumber}`;
                                    this.$notification(NotificationOptions.notificationOptionsPreset(
                                        `Failed to print Rx ${jobId}: ${progress.failureMessage}`,
                                        NotificationOptions.NotificationTypes.danger
                                    ));
                                }

                                // Handle mark-as-complete failures immediately
                                if (progress.markCompleteFailedJob) {
                                    const job = progress.markCompleteFailedJob;
                                    const rxId = `${job.storeId}-${job.rxNumber}-${job.rfNumber}`;
                                    console.log(`Mark as complete failed for Rx ${rxId}, retrying with fresh token`);

                                    // Retry marking as complete with fresh token from frontend
                                    try {
                                        await Axios.post("/Print/MarkAsPrinted", {
                                            storeId: job.storeId,
                                            rxNumber: job.rxNumber,
                                            rfNumber: job.rfNumber,
                                            orderId: job.orderId
                                        });
                                        console.log(`Successfully marked Rx ${rxId} as printed via retry`);
                                    } catch (err) {
                                        console.error(`Failed to mark Rx ${rxId} as printed even after retry`, err);
                                        this.$notification(NotificationOptions.notificationOptionsPreset(
                                            `Rx ${rxId} was printed but could not be marked as complete`,
                                            NotificationOptions.NotificationTypes.warning
                                        ));
                                    }
                                }
                            }
                        } else if (e.data[0] === "clientServicePrintResponse") {
                            const data = e.data[1];
                            // Only handle response for our job
                            if (data.jobId === jobId) {
                                workerPort.removeEventListener("message", handleMessage);

                                // Clear progress when complete
                                this.printProgress = null;

                                // Check if we should fallback to HTTP
                                if (data.shouldFallback) {
                                    console.log("WebSocket printing failed, falling back to HTTP");
                                    resolve({ success: false });
                                    return;
                                }

                                resolve({ success: true, data });
                            }
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    workerPort.postMessage(["clientServicePrint", localRequest]);
                });

                // If WebSocket succeeded, show notifications and return
                if (result.success && result.data) {
                    const data = result.data;

                    // Note: Mark-complete failures are already handled in progress updates
                    // No need to retry them here

                    const errorCount = data.errors?.length ?? 0;
                    const printedCount = jobsToPrint.length - errorCount;

                    // Only show summary notification for successful prints
                    // Failures were already notified during progress updates
                    if (printedCount > 0) {
                        this.$notification(NotificationOptions.notificationOptionsPreset(`Printed ${printedCount} successfully`, NotificationOptions.NotificationTypes.success));
                    }

                    await this.loadOrders();
                    return;
                }

                // If we got here, we need to fallback to HTTP
                console.log("Falling back to HTTP printing");
            }

            // Fallback to HTTP if WebSocket not available or failed
            const localResponse = await Axios.post("http://localhost:7274/print", localRequest);

            const data = localResponse.data;
            const errorCount = data.errors?.length ?? 0;
            const printedCount = jobsToPrint.length - errorCount;

            if (localResponse.data.errors?.length) {
                this.$notification(NotificationOptions.notificationOptionsPreset(`${errorCount} has issues printing`, NotificationOptions.NotificationTypes.danger));
            }

            if (printedCount > 0) {
                this.$notification(NotificationOptions.notificationOptionsPreset(`Printed ${printedCount} successfully`, NotificationOptions.NotificationTypes.success));
            }

            await this.loadOrders();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            // Always clear progress when done
            this.printProgress = null;
        }
    }

    async getAuthToken(): Promise<string | null> {
        try {
            // Check auth provider type
            const authProviderType = (this as any).$authProviderType;

            if (authProviderType === "Cognito") {
                // Get Cognito token
                const { getToken } = await import("@/util/cognitoAuth");
                return await getToken();
            } else {
                // Get MSAL/OIDC token
                const MicrosoftAuth = (await import("@/util/microsoftAuth")).default;
                const token = await MicrosoftAuth.fetchToken();
                return token.accessToken;
            }
        } catch (err) {
            console.error("Failed to get auth token", err);
            return null;
        }
    }

    async removeWithoutPrinting() {
        try {
            const ids = {
                Labels: this.selectedRxsOut.map(rx => {
                    return {
                        OrderId: rx.orderId,
                        StoreId: rx.storeID,
                        RxNumber: rx.rxNumber,
                        RfNumber: rx.rfNumber,
                    };
                }),
            };
            await Axios.post(`/Print/RemoveFromPrintQueue`, ids);
            this.$notification(NotificationOptions.notificationOptionsPreset(`Labels Removed`, NotificationOptions.NotificationTypes.success));
            await this.loadOrders();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async printByRxRf() {
        this.rxToPrint.storeID = Number(this.storeFilter.id);
        this.rxToPrint.rfNumber = this.rxToPrint.rfNumber || 0;
        console.log('this.rxToPrint', this.rxToPrint.rxID);
        if (!this.rxToPrint.hasFullId) return;

        this.isPrintingRxRf = true;

        const localPrint = !!this.activeLocalPrinter;

        try {
            const res = await Axios.get(`/Print/${this.rxToPrint.rxID}/rx-pated`, { params: { localPrint } });

            if (localPrint) {
                await this.sendToLocalPrint(res.data.toPrint, false);
                return;
            }

            if (res.data.printedLabels > 0 && res.data.badLabels?.length == 0) {
                const msg = `PatEd for Rx ${this.rxToPrint.rxID} was successfully printed`;
                this.$notification(NotificationOptions.notificationOptionsPreset(msg, NotificationOptions.NotificationTypes.success));
            }
        } catch (error) {
            const err = error as any;
            if ((err?.response?.data as string).includes("No Prescription found"))
                this.$notification(NotificationOptions.notificationOptionsPreset("Prescription not found", NotificationOptions.NotificationTypes.danger));
            else
                this.$notification(NotificationOptions.error(error));
        } finally {
            this.isPrintingRxRf = false;
        }
    }

    async printBadge() {
        try {
            this.isPrintingBadge = true;

            const config: AxiosRequestConfig = {
                params: {
                    storeId: this.storeFilter.id,
                    ipAddress: this.activeBadgePrinter ? null : this.ipAddress
                }
            };

            const response = await Axios.post<string>(`/Print/PrintMyBadge`, null, config);

            if (!this.activeBadgePrinter) return;

            const rawPrintRequest = {
                printerName: this.activeBadgePrinter.name,
                rawContent: response.data,
                documentName: "badge-reprint",
                copies: 1
            };

            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Use WebSocket connection through shared worker for raw printing
                await new Promise<void>((resolve, reject) => {
                    // Note: clientServicePrintRaw doesn't have a response handler in the worker,
                    // so we just fire and forget with a small delay
                    workerPort.postMessage(["clientServicePrintRaw", rawPrintRequest]);
                    setTimeout(resolve, 500);
                });
                return;
            }

            // Fallback to HTTP if WebSocket not available
            const printerEndpoint = "http://localhost:7274/print/raw";
            await Axios.post(printerEndpoint, rawPrintRequest);
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isPrintingBadge = false;
        }
    }

    async sendOutOfStock(e: any) {
        e.preventDefault();

        try {
            const res = await Axios.post<number>(`/Print/${this.outOfStockValue}/OutOfStock`, null, { params: { shouldOverride: this.shouldOverride } });
            const newStockQuantity = res.data;
            const message = `Marked out of stock. Net available: ${newStockQuantity}`;

            this.$notification(NotificationOptions.notificationOptionsPreset(message, NotificationOptions.NotificationTypes.success));
            this.needsOverride = false;
            this.shouldOverride = false;
            await this.loadOrders();
        } catch (err) {
            this.needsOverride = true;
            this.$notification(NotificationOptions.errorSaveNotificationPreset("Out Of Stock", err as AxiosError));
        } finally {
            this.outOfStockValue = '';
        }
    };

    rxID(prescription: Prescription | null): string {
        if (!prescription) return "";
        return `${prescription.storeID}-${prescription.rxNumber}-${prescription.rfNumber}`;
    }

    // FILTERS
    search() {
        this.loadOrders();
    }

    clearFilters() {
        this.showConsignment = false;
        this.$router.replace({
            name: this.$route.name ?? "",
            query: {
                storeId: this.storeFilter.id?.toString(),
                preserveScroll: "1",
                oos: "false",
                recent: "false",
                programId: null
            }
        });
    }

    colorCodeTable(rx: Prescription, type: any) {
        if (!rx || type !== "row") return;

        if (this.showPrinted) return 'table-info';
        if (rx.deliveryCode == DeliveryCode.WillCall) return 'table-warning';
        if (this.priorityCodes.includes(rx.deliveryCode)) return 'table-danger';
        const dayDiff = moment(rx.expirationDate).diff(moment(), "days");
        if (dayDiff <= 1) return 'table-danger';
        if (dayDiff <= 3) return 'table-warning';
        if (dayDiff <= 7) return 'table-info';
    }

    async getLocalPrinters() {
        try {
            this.isLoadingPrinters = true;

            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Request printers through the shared worker
                const printers = await new Promise<any[]>((resolve) => {
                    const timeout = setTimeout(() => {
                        console.log("WebSocket printer request timed out, falling back to HTTP");
                        resolve([]);
                    }, 3000);

                    const handleMessage = (e: MessageEvent) => {
                        if (e.data[0] === "clientServicePrinters") {
                            clearTimeout(timeout);
                            workerPort.removeEventListener("message", handleMessage);
                            resolve(e.data[1] || []);
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    workerPort.postMessage(["clientServiceRefreshPrinters", null]);
                });

                if (printers.length > 0) {
                    this.localPrinters = printers;
                    this.handleDefaultPrinter();
                    return;
                }
            }

            // Fallback to HTTP if WebSocket not available or no printers received
            const response = await this.$http.get("http://localhost:7274/print/devices");
            this.localPrinters = response.data;

            this.handleDefaultPrinter();
        } catch {
            //ignore
        } finally {
            this.isLoadingPrinters = false;
        }
    }

    handleDefaultPrinter() {
        const lastPrinter = localStorage.getItem("active-printer");
        const lastBadgePrinter = localStorage.getItem("active-badge-printer");

        if (lastPrinter) {
            this.activeLocalPrinter = this.localPrinters?.find(p => p.name === lastPrinter);
        }

        if (lastBadgePrinter) {
            this.activeBadgePrinter = this.localPrinters?.find(p => p.name === lastBadgePrinter);
        }
    }

    get hasLocalPrinters() {
        return !!this.localPrinters.length;
    }

    prettyPrinterName(printer: any) {
        if (!printer && (!this.storeFilter || this.storeFilter.defaultPrinter)) return "Store Default Printer";
        if (!printer) return "No Printer Defined";
        if (!printer.serverName) return printer.name;
        return printer.name.replace(printer.serverName, "").replace(/\\/g, "");
    }

    get showPrintingModal() {
        return this.isPrinting || this.isPrintingRxRf || this.isPrintingBadge;
    }

    get selectedCount() {
        return this.selectedRxsOut?.length;
    }

    async markInStock() {
        try {
            this.isMarkingInStock = true;

            const selectedScripts = this.selectedRxsOut.map(rx => {
                return {
                    OrderId: rx.orderId,
                    StoreId: rx.storeID,
                    RxNumber: rx.rxNumber,
                    RfNumber: rx.rfNumber,
                    ProgramId: rx.programID,
                };
            });

            await Axios.post("/Print/MarkAsInStock", selectedScripts);

            await this.loadOrders();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.isMarkingInStock = false;
        }
    }


    // Transfer handling methods
    onTransferCompleted(_transferData: any) {
        this.loadOrders(); // Reload the queue after transfer
    }

    // Column customization methods
    loadColumnPreferences() {
        const savedPreferences = localStorage.getItem('printQueueColumnPreferences');
        if (savedPreferences) {
            try {
                const saved = JSON.parse(savedPreferences);
                // Merge saved preferences with defaults to ensure new fields have proper defaults
                this.columnVisibility = {
                    // Core table fields defaults
                    select: true,
                    hasAddons: true,
                    productNameShort: true,
                    patientName: true,
                    storeName: true,
                    rxId: true,
                    daySupply: true,
                    dispensed: true,
                    qtyLeft: true,
                    fillDate: true,
                    patientPay: true,
                    programName: true,
                    edit: true,
                    // Additional fields defaults
                    transferToPharmacy: true,
                    orderDate: true,
                    addressState: true,
                    addressZip: true,
                    drugsFilledDate: true,
                    ndc: true,
                    rxIdWithExpirationWarning: true,
                    promisedDate: true,
                    // Override with saved preferences
                    ...saved
                };
            } catch (err) {
                console.error('Failed to load column preferences:', err);
            }
        }
    }

    saveColumnPreferences() {
        try {
            localStorage.setItem('printQueueColumnPreferences', JSON.stringify(this.columnVisibility));
            this.$notification(NotificationOptions.notificationOptionsPreset(
                'Column preferences saved successfully',
                NotificationOptions.NotificationTypes.success
            ));
        } catch (err) {
            console.error('Failed to save column preferences:', err);
            this.$notification(NotificationOptions.errorSaveNotificationPreset('Column Preferences', err as AxiosError));
        }
    }
}

</script>

<style scoped lang="scss">

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 200px), 1fr));
}

::v-deep(.dropdown-item.active) {
    background-color: rgba(blue, .05);
    font-weight: bold;
}

@keyframes fadeIn {
    from {
        opacity: 1;
        zoom: 1;
    }
}

.printers-loading {
    font-size: 3.5em;
    gap: 10px;
    height: 100px;
}

.printers-loading>i {
    animation: fadeIn 1.5s infinite;
    opacity: 0;
    width: 60px;
    zoom: .85;
    animation-delay: 1.5s;
}

.printers-loading>i:nth-child(1) {
    animation-delay: .5s;
}

.printers-loading>i:nth-child(2) {
    animation-delay: 1s;
}

.printers-loading>i:last-child {
    animation: none;
    opacity: 1;
    font-size: 1.3em;
}

.print-actions {
    position: sticky;
    top: 0;
    z-index: 100;
}

@keyframes throb {

    0%,
    100% {
        transform: scale(1);
        opacity: 0.9;
    }

    50% {
        transform: scale(1.03);
        opacity: 1;
    }
}

.throb-badge {
    animation: throb 1.5s ease-in-out infinite;
    /* Apply the animation */
}
</style>
